/**
 * AnnotationsList Component
 * 
 * Displays a filterable timeline of session flag annotations with review and management tools.
 * Designed for proctors and reviewers to efficiently review and take action on flagged events
 * during exam session playback.
 * 
 * Features:
 * - Timeline visualization of flag annotations with severity indicators
 * - Bulk edit mode for batch approval/deletion of flags
 * - Reviewer tools for annotation management
 * - Auto-scroll to follow current playback position
 * - Filter for unreviewed annotations
 * - Keyboard shortcuts for common actions
 * - Permission-based access control for sensitive operations
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import './styles.css';
import {
  View,
  Alert,
  Button,
  IconVideoCameraSolid,
  IconButton,
  Checkbox,
  IconXSolid,
  IconCertifiedLine,
  IconTrashLine,
  Flex,
  IamProtected,
  Pill,
  Text,
  Link,
} from '@smarterservices/smarter-ui';
import Timeline from '../../Common/Timeline';
import Loader from '@ui/context/session/components/Common/Loader';
import { useExamSessionStore, usePlayerStore, getStore } from '../../../stores/StoreContext';
import { formatTime, formatDuration } from '../../../helpers/utilities';
import CONSTANTS from '../../../config/constants';
import LineDetail from './LineDetail';
import ButtonWithConfirmation from '@ui/shared/components/Common/ButtonWithConfirmation';
import { NavigationalToolTip } from '@ui/shared/components/Common/NavigationalToolTip';
import { useHotkeys } from 'react-hotkeys-hook';

/**
 * AnnotationTitle Component
 * 
 * Renders the title section of a flag annotation timeline item.
 * Displays the annotation type name and an optional duration pill.
 * 
 * @param {Object} annotation - The annotation object containing type, duration, and range info
 */
const AnnotationTitle = React.memo(({ annotation }) => {
  let duration;
  let pillColor = "primary";
  if (annotation.duration) {
    duration = formatDuration(annotation.duration);
  }

  if (!annotation.duration && annotation.range.start && !annotation.range.end) {
    duration = 'Ongoing';
    pillColor = "danger";
  }

  if (!duration) {
    return <span>{annotation.type.name}</span>;
  }

  return (
    <Flex gap="x-small" alignItems="center">
      <Flex.Item>
        <span>{annotation.type.name}</span>
      </Flex.Item>
      <Flex.Item>
        <Pill color={pillColor} margin="none">
          <Text variant="legend">{duration}</Text>
        </Pill>
      </Flex.Item>
    </Flex>
  );
});

/**
 * AnnotationsList Component
 * Main component for displaying and managing flag annotations
 */
const AnnotationsList = () => {
  // Constants
  const menuHeight = 55;
  const types = ['flags']; // Only show flag annotations

  // State: Data Management
  const [timelineData, setTimelineData] = useState([]); // Processed timeline items
  const [initialized, setInitialized] = useState(false); // Track if initial data load is complete

  // State: UI Controls
  const [showReviewerTools, setShowReviewerTools] = useState(false); // Show reviewer action buttons
  const [showDetails, setShowDetails] = useState(true); // Show annotation details
  const [showUnreviewed, setShowUnreviewed] = useState(false); // Filter for unreviewed annotations
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true); // Auto-scroll with playback
  const [autoscrollTopOffset, setAutoscrollTopOffset] = useState(menuHeight); // Offset for auto-scroll positioning

  // State: Bulk Edit
  const [bulkEdit, setBulkEdit] = useState(false); // Bulk edit mode enabled
  const [bulkChangeInProcess, setBulkChangeInProcess] = useState(false); // Bulk operation in progress
  const [bulkChangeType, setBulkChangeType] = useState(null); // Type of bulk operation (approve/delete)
  const [bulkSelectedItems, setBulkSelectedItems] = useState([]); // Selected annotation IDs
  const [bulkChangePercentage, setBulkChangePercentage] = useState(0); // Progress percentage
  const [bulkChangeCompleted, setBulkChangeCompleted] = useState(false); // Operation completed flag
  const [selectAll, setSelectAll] = useState(false); // Select all checkbox state
  const [bulkSelectedItemsCompleted, setBulkSelectedItemsCompleted] = useState([]); // Bulk selected items completed flag

  // State: Progressive Rendering
  const [isRenderingTools, setIsRenderingTools] = useState(false); // Progressive rendering flag for reviewer tools

  // Refs
  const timelineRef = useRef(); // Reference to Timeline component
  const controlsRef = useRef(); // Reference to controls container

  const ExamSessionStore = getStore('examSessionStore');
  const PlayerStore = getStore('playerStore');
  const AppStore = getStore('appStore');

  // Store State Subscriptions
  const annotations = useExamSessionStore((state) => state.annotations); // All session annotations
  const displayMarkers = usePlayerStore((state) => state.displayMarkers); // Active marker filters
  const currentTime = usePlayerStore((state) => state.currentTime); // Current playback time

  /**
   * Keyboard shortcut: Toggle unreviewed filter
   */
  useHotkeys(
    CONSTANTS.KEYBOARD_SHORTCUTS.ANNOTATIONS.OPTIONS.ONLY_UNREVIEWED_TOGGGLE.KEY,
    (event) => {
      event.preventDefault();
      setShowUnreviewed(prev => !prev);
    },
    {
      enableOnContentEditable: false,
      preventDefault: true
    },
    []
  );

  /**
   * Keyboard shortcut: Toggle annotation details
   */
  useHotkeys(
    CONSTANTS.KEYBOARD_SHORTCUTS.ANNOTATIONS.OPTIONS.SHOW_DETAILS.KEY,
    (event) => {
      event.preventDefault();
      setShowDetails(prev => !prev);
    },
    {
      enableOnContentEditable: false,
      preventDefault: true
    },
    []
  );

  /**
   * Memoized handler for toggling reviewer tools with progressive rendering
   * Enables details view when reviewer tools are shown
   */
  const handleToggleReviewerTools = useCallback(() => {
    setShowReviewerTools(prev => {
      const newValue = !prev;
      if (newValue) {
        setShowDetails(true);
        // Progressive rendering: show tools after a small delay
        setIsRenderingTools(false);
        setTimeout(() => setIsRenderingTools(true), 16); // Next frame
      } else {
        setIsRenderingTools(false);
      }
      return newValue;
    });
  }, []);

  /**
   * Keyboard shortcut: Toggle reviewer tools
   */
  useHotkeys(
    CONSTANTS.KEYBOARD_SHORTCUTS.ANNOTATIONS.OPTIONS.SHOW_REVIEWER_TOOLS.KEY,
    (event) => {
      event.preventDefault();
      handleToggleReviewerTools();
    },
    {
      enableOnContentEditable: false,
      preventDefault: true
    },
    [handleToggleReviewerTools]
  );

  /**
   * Keyboard shortcut: Toggle bulk edit mode
   */
  useHotkeys(
    CONSTANTS.KEYBOARD_SHORTCUTS.ANNOTATIONS.OPTIONS.BULK_EDIT.KEY,
    (event) => {
      event.preventDefault();
      setBulkEdit(prev => !prev);
    },
    {
      enableOnContentEditable: false,
      preventDefault: true
    },
    []
  );

  /**
   * Keyboard shortcut: Toggle auto-scroll
   */
  useHotkeys(
    CONSTANTS.KEYBOARD_SHORTCUTS.ANNOTATIONS.OPTIONS.AUTO_SCROLL.KEY,
    (event) => {
      event.preventDefault();
      setAutoScrollEnabled(prev => !prev);
    },
    {
      enableOnContentEditable: false,
      preventDefault: true
    },
    []
  );

  /**
   * Clear selections when bulk edit is disabled and update autoscroll offset
   */
  useEffect(() => {
    if (!bulkEdit) {
      setBulkSelectedItems([]);
      setSelectAll(false);
    }

    // Update autoscroll offset after DOM has rendered
    const updateOffset = () => {
      if (controlsRef.current) {
        const rect = controlsRef.current.getBoundingClientRect();
        if (rect.height > 0) {
          setAutoscrollTopOffset(rect.height + 5);
        }
      }
    };

    // Use setTimeout to ensure DOM has rendered
    const timeoutId = setTimeout(updateOffset, 100);

    return () => clearTimeout(timeoutId);
  }, [bulkEdit]);

  /**
   * Memoized timeline item for recording start marker
   * Only shown if annotations exist before recording started (negative timestamps)
   */
  const recordingStartedOnTimeline = useMemo(() => ({
    icon: <IconButton
      as="button"
      cursor="pointer"
      interaction="enabled"
      shape="rectangle"
      screenReaderLabel="Jump to recording start"
      margin="xxx-small"
      color="primary"
      size="small"
      className='timeline-icon-button'
      onClick={() => PlayerStore.jumpTo(0)}
    >
      <IconVideoCameraSolid />
    </IconButton>,
    label: <View as="div" textAlign="end" width={bulkEdit ? "80px" : "65px"}>{formatTime(0)}</View>,
    title: "Recording Started",
    time: 0,
    detail: <LineDetail hideMenu={true} reviewerTools={showReviewerTools} details={showDetails} annotation={
      {
        details: "",
        createdBy: "system",
      }
    } />,
  }), [bulkEdit, showReviewerTools, showDetails, PlayerStore]);

  /**
   * Build timeline data from flag annotations
   * Processes annotations and converts them to timeline items with proper formatting,
   * severity indicators, and interactive handlers
   */
  useEffect(() => {
    const tempTimelineData = [];
    let allAnnotations = ExamSessionStore.getAnnotations({ types });
    let recordingStartedMarker = false;

    // Remove any annotations where the start is less than 0
    allAnnotations = allAnnotations.filter((annotation) => annotation.start >= 0);

    // if the onlyUnreviewed is true, then we need to filter where the annotation.reviewedDate is null
    if (showUnreviewed) {
      allAnnotations = allAnnotations.filter((annotation) => !annotation.reviewedDate);
    }

    for (const annotation of allAnnotations) {

      if (annotation.start < 0 && !recordingStartedMarker) {
        tempTimelineData.push(recordingStartedOnTimeline);
        recordingStartedMarker = true;
      }

      // Build theme override for custom severity colors
      const severity = PlayerStore.flagScoreToSeverity(annotation.score);
      const themeOverride = {
        [`${severity.color}Background`]: severity.backgroundColor,
        [`${severity.color}Color`]: severity.iconColor
      };

      const annotationOnTimeline = {
        id: annotation.sid,
        icon: <IconButton
          as="button"
          cursor="pointer"
          interaction="enabled"
          shape="circle"
          screenReaderLabel="Jump to annotation start"
          margin="xxx-small"
          color={severity.color}
          size="small"
          className='timeline-icon-button'
          onClick={() => PlayerStore.jumpTo(
            annotation.start >
              CONSTANTS.ANNOTATIONS.SECONDS_LEADING_TO_ANNOTATION
              ? annotation.start -
              CONSTANTS.ANNOTATIONS.SECONDS_LEADING_TO_ANNOTATION
              : 0
          )}
          themeOverride={themeOverride}
        >
          {severity.icon}
        </IconButton>,
        label:
          <Flex as="div" justifyItems="end" width={bulkEdit ? "80px" : "65px"}>
            <Flex.Item width="20px">
              {bulkEdit && (<Checkbox
                size="small"
                checked={bulkSelectedItems.includes(annotation.sid)}
                value={annotation.sid}
                onChange={() => {
                  let newSelectedItems;
                  if (bulkSelectedItems.includes(annotation.sid)) {
                    newSelectedItems = bulkSelectedItems.filter(id => id !== annotation.sid);
                  } else {
                    newSelectedItems = [...bulkSelectedItems, annotation.sid];
                  }
                  setBulkSelectedItems(newSelectedItems);

                  // Update selectAll state based on selection count
                  const totalAnnotations = ExamSessionStore.getAnnotations({ types }).length;
                  setSelectAll(newSelectedItems.length === totalAnnotations);
                }}
              />)}
            </Flex.Item>
            <Flex.Item shouldGrow textAlign="end">
              {annotation.start < 0 ? "" : formatTime(annotation.start)}
            </Flex.Item>
          </Flex>,
        title: <AnnotationTitle annotation={annotation} />,
        time: annotation.start,
        detail: <LineDetail annotation={annotation} reviewerTools={false} details={showDetails} />,
      };
      tempTimelineData.push(annotationOnTimeline);
    }
    setTimelineData(tempTimelineData);
    setInitialized(true);
  }, [annotations, displayMarkers, bulkEdit, bulkSelectedItems, bulkChangeCompleted, showUnreviewed, ExamSessionStore, PlayerStore, recordingStartedOnTimeline, showReviewerTools, showDetails]);

  /**
   * Memoized timeline data with UI state
   * Clones timeline items with current UI state for reviewer tools and details visibility
   */
  const timelineDataWithUIState = useMemo(() => {
    return timelineData.map(item => ({
      ...item,
      detail: item.detail ? React.cloneElement(item.detail, {
        reviewerTools: showReviewerTools && isRenderingTools,
        details: showDetails
      }) : item.detail
    }));
  }, [timelineData, showReviewerTools, showDetails, isRenderingTools]);

  /**
   * Memoized handler for toggling details
   */
  const handleToggleDetails = useCallback(() => {
    setShowDetails(prev => !prev);
  }, []);

  /**
   * Handle bulk change operations (approve/delete)
   * Processes annotations in batches to avoid overwhelming the server
   * @param {Object} options - Options object
   * @param {string} options.changeType - Type of change: 'approve' or 'delete'
   */
  const handleBulkChange = useCallback(async ({ changeType }) => {
    setBulkChangeType(changeType);
    setBulkChangeInProcess(true);
    setBulkSelectedItemsCompleted([]);
    setBulkChangePercentage(0);
    setBulkEdit(false);
    setBulkChangeCompleted(false);

    try {
      // Process in batches of 10 to avoid overwhelming the server
      const batchSize = 3;
      const batches = [];

      // Split bulkSelectedItems into chunks of 10
      for (let i = 0; i < bulkSelectedItems.length; i += batchSize) {
        batches.push(bulkSelectedItems.slice(i, i + batchSize));
      }

      // Process each batch sequentially
      let completedCount = 0;
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const updatePromises = batch.map(annotationId => {
          if (changeType === "approve") {
            const changePayload = {};
            changePayload.reviewedBy = AppStore.user.username;
            changePayload.reviewedDate = new Date().toISOString();
            changePayload.updatedBy = AppStore.user.username;
            changePayload.updatedDate = new Date().toISOString();
            ExamSessionStore.updateAnnotation(annotationId, changePayload);
          } else if (changeType === "delete") {
            ExamSessionStore.deleteAnnotation(annotationId);
          }
        });

        // Wait for this batch to complete before starting the next
        await Promise.all(updatePromises);

        // Update progress based on completed batches
        completedCount += batch.length;
        setBulkSelectedItemsCompleted(prev => [...prev, ...batch]);
        setBulkChangePercentage(Math.round((completedCount / bulkSelectedItems.length) * 100));

      }
      await ExamSessionStore.loadAnnotations();
    } catch (error) {
      console.error('Error during bulk change:', error);
    } finally {
      setBulkEdit(false);
      setBulkSelectedItems([]);
      setSelectAll(false);
      setBulkChangeInProcess(false);
      setBulkChangeCompleted(true);
    }
  }, [bulkSelectedItems, ExamSessionStore]);

  /**
   * Memoized handler for bulk approve
   */
  const handleBulkApprove = useCallback(async () => {
    return await handleBulkChange({ changeType: "approve" });
  }, [handleBulkChange]);

  /**
   * Memoized handler for bulk delete
   */
  const handleBulkDelete = useCallback(async () => {
    return await handleBulkChange({ changeType: "delete" });
  }, [handleBulkChange]);

  /**
   * Memoized handler for auto-scroll toggle
   */
  const handleAutoScrollToggle = useCallback(() => {
    setAutoScrollEnabled(prev => !prev);
  }, []);

  /**
   * Memoized handler for unreviewed filter toggle
   */
  const handleUnreviewedToggle = useCallback(() => {
    setShowUnreviewed(prev => !prev);
  }, []);

  // Show loader while initializing
  if (!initialized) {
    return (
      <View as="div" margin="medium 0 0 0">
        <Loader
          spinnerSize="small"
          color="primary"
          textSize="medium"
          text='Loading session flags'
        />
      </View>
    );
  }

  return (
    <View as="div" position="relative">
      {/* Display timeline if annotations exist */}
      {timelineDataWithUIState.length ? (
        <>
          <View as="div"
            className="auto-scroll-toggle"
            background="primary"
            elementRef={(ref) => {
              controlsRef.current = ref;
            }}
          >
            <View
              as="div"
              background="secondary"
              borderColor="primary"
              borderRadius="medium"
              borderWidth="small"
              padding="x-small"
              margin="none none xx-small none"
            >
              <Flex justifyItems="space-between" alignItems="center">
                <Flex.Item>
                  <IamProtected
                    description="Protects the reviewer toggles for annotations."
                    permissionsOr={[
                      {
                        action: 'sp:ModifyExamAnnotation',
                        resource: AppStore.getIamResourceString(`session/${ExamSessionStore.sessionSid}`)
                      },
                      {
                        action: 'sp:DeleteExamAnnotation',
                        resource: AppStore.getIamResourceString(`session/${ExamSessionStore.sessionSid}`)
                      }
                    ]}
                  >
                    <NavigationalToolTip
                      description="Only show the unreviewed annotations"
                      placement="bottom start"
                      shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.ANNOTATIONS?.OPTIONS?.ONLY_UNREVIEWED_TOGGGLE?.KEY}
                    >
                      <Checkbox
                        label="Unreviewed"
                        variant="toggle"
                        size="small"
                        labelPlacement="end"
                        checked={showUnreviewed}
                        onChange={handleUnreviewedToggle}
                        inline
                      />
                    </NavigationalToolTip>

                    <View padding="none none none small">
                      <NavigationalToolTip
                        description="Toggle reviewer tools"
                        placement="bottom start"
                        shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.ANNOTATIONS?.OPTIONS?.SHOW_REVIEWER_TOOLS?.KEY}
                      >
                        <Checkbox
                          label="Tools"
                          variant="toggle"
                          size="small"
                          labelPlacement="end"
                          checked={showReviewerTools}
                          onChange={handleToggleReviewerTools}
                          inline
                        />
                      </NavigationalToolTip>
                    </View>
                  </IamProtected>

                  <View padding="none none none small">
                    <NavigationalToolTip
                      description="Toggle annotation details"
                      placement="bottom start"
                      shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.ANNOTATIONS?.OPTIONS?.SHOW_DETAILS?.KEY}
                    >
                      <Checkbox
                        label="Details"
                        variant="toggle"
                        size="small"
                        labelPlacement="end"
                        checked={showDetails}
                        onChange={handleToggleDetails}
                        inline
                      />
                    </NavigationalToolTip>
                  </View>
                </Flex.Item>
                <Flex.Item>
                  <IamProtected
                    permissionsOr={[
                      {
                        action: 'sp:ModifyExamAnnotation',
                        resource: AppStore.getIamResourceString(`session/${ExamSessionStore.sessionSid}`)
                      },
                      {
                        action: 'sp:DeleteExamAnnotation',
                        resource: AppStore.getIamResourceString(`session/${ExamSessionStore.sessionSid}`)
                      }
                    ]}
                  >
                    {!bulkEdit && (
                      <NavigationalToolTip
                        description="Bulk edit annotations"
                        shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.ANNOTATIONS?.OPTIONS?.BULK_EDIT?.KEY}
                      >
                        <Button
                          size="small"
                          screenReaderLabel={bulkEdit ? "Disable bulk edit" : "Enable bulk edit"}
                          onClick={() => setBulkEdit(!bulkEdit)}
                          color={bulkEdit ? "success" : "primary"}
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(4px)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            border: '1px solid rgba(0, 0, 0, 0.1)'
                          }}
                        >
                          Bulk Edit
                        </Button>
                      </NavigationalToolTip>
                    )}
                  </IamProtected>
                  <View padding="none none none x-small">
                    <NavigationalToolTip
                      description="Auto scroll"
                      placement="bottom start"
                      shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.ANNOTATIONS?.OPTIONS?.AUTO_SCROLL?.KEY}
                    >
                      <Checkbox
                        label="Auto Scroll"
                        variant="toggle"
                        size="small"
                        inline={true}
                        labelPlacement="end"
                        checked={autoScrollEnabled}
                        onChange={handleAutoScrollToggle}
                        margin="none none none x-small"
                      />
                    </NavigationalToolTip>
                  </View>
                </Flex.Item>
              </Flex>
            </View>
            {bulkChangeInProcess && (
              <Alert size="small">
                {bulkChangePercentage}% of the annotations have been {bulkChangeType === "approve" ? "approved" : "deleted"}.
              </Alert>
            )}
            {bulkChangeCompleted && (
              <Alert variant="success" size="small" timeout={5000}>
                All selected annotations have been {bulkChangeType === "approve" ? "set as reviewed" : "deleted"}.
              </Alert>
            )}
            {bulkEdit && (
              <View
                as="div"
                className="bulk-edit-options"
                background="secondary"
                borderColor="primary"
                borderRadius="medium"
                borderWidth="small"
                padding="x-small"
                margin="none none xx-small none"
              >
                <Flex justifyItems="space-between">
                  {!bulkChangeInProcess && (
                    <Flex.Item>
                      {bulkSelectedItems.length === ExamSessionStore.getAnnotations({ types }).length ? (
                        <Button size="small" onClick={() => {
                          setBulkSelectedItems([]);
                          setSelectAll(false);
                        }}>Deselect All</Button>
                      ) : (
                        <Button size="small" onClick={() => {
                          const allAnnotations = ExamSessionStore.getAnnotations({ types });
                          const allAnnotationIds = allAnnotations.map(annotation => annotation.sid);
                          setBulkSelectedItems(allAnnotationIds);
                          setSelectAll(true);
                        }}>Select All</Button>
                      )}

                      {bulkSelectedItems.length > 0 && (
                        <>
                          <ButtonWithConfirmation
                            margin="none none none x-small"
                            onConfirm={() => handleBulkApprove()}
                            confirmationText={`Mark ${bulkSelectedItems.length} item${bulkSelectedItems.length > 1 ? 's' : ''} as reviewed?`}
                            confirmButtonText="Yes"
                            confirmButtonColor="success"
                            color="success"
                            screenReaderLabel={`Mark ${bulkSelectedItems.length} item${bulkSelectedItems.length > 1 ? 's' : ''} as reviewed?`}
                          >
                            <IconCertifiedLine />
                          </ButtonWithConfirmation>
                          <ButtonWithConfirmation
                            margin="none none none x-small"
                            onConfirm={() => handleBulkDelete()}
                            confirmationText={`Delete ${bulkSelectedItems.length} selected item${bulkSelectedItems.length > 1 ? 's' : ''}?`}
                            confirmButtonText="Yes"
                            confirmButtonColor="danger"
                            color="danger"
                            screenReaderLabel={`Delete ${bulkSelectedItems.length} selected item${bulkSelectedItems.length > 1 ? 's' : ''}?`}
                          >
                            <IconTrashLine />
                          </ButtonWithConfirmation>
                        </>
                      )}
                    </Flex.Item>
                  )}
                  <Flex.Item alignItems="end">
                    <NavigationalToolTip
                      description="Close bulk edit"
                      shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.ANNOTATIONS?.OPTIONS?.BULK_EDIT?.KEY}
                    >
                      <IconButton
                        size="small"
                        screenReaderLabel="Exit Bulk Edit Mode"
                        onClick={() => setBulkEdit(false)}
                      >
                        <IconXSolid />
                      </IconButton>
                    </NavigationalToolTip>
                  </Flex.Item>
                </Flex>
              </View>
            )}
          </View>

          {/* Timeline component */}
          <Timeline
            ref={timelineRef}
            items={timelineDataWithUIState}
            currentTime={currentTime}
            autoScroll={autoScrollEnabled}
            showAutoScrollToggle={false}
            topOffset={autoscrollTopOffset}
            highlightScrollTarget={true}
          />
        </>
      ) : (
        /* Empty state messages */
        <>
          {showUnreviewed ? (
            <Alert variant="info" transition="none">
              There are no unreviewed flags associated with this session.
              <Link onClick={handleUnreviewedToggle}>Remove the filter</Link> to show all annotations.
            </Alert>
          ) : (
            <Alert variant="success" transition="none" margin="medium none none none">
              There are no flags associated with this session.
            </Alert>
          )}
        </>
      )}
    </View>
  );
};

export default AnnotationsList;
